import { App } from 'antd';
import { useCallback, useEffect, useRef } from 'react';
import { schedulesApi } from '@/api/endpoints';
import { formatDayMonth, type CalendarDate } from '@/domain/dates';
import { useSessionStore } from '@/stores/session-store';

/**
 * Nhờ MÁY CHỦ sinh việc hôm nay từ lịch hẹn (ADR-0006), rồi nạp lại dữ liệu nếu có dòng mới.
 * Gọi thừa vô hại nên cứ gọi ở mọi mốc mà bản cũ từng chạy `syncSched`.
 */
export function useGenerateScheduledTasks(): () => Promise<number> {
  const reload = useSessionStore((state) => state.reload);
  const readOnly = useSessionStore((state) => state.user?.permissions.readOnly ?? false);
  return useCallback(async () => {
    // Tài khoản chỉ xem (07/10/2026): máy chủ từ chối mọi lệnh ghi — không gọi cho tốn một lượt 403.
    if (readOnly) return 0;
    try {
      const { created, disabledSchedules } = await schedulesApi.generateToday();
      if (created || disabledSchedules) await reload();
      return created;
    } catch {
      // Không sinh được việc theo lịch không đáng để chặn cả màn hình; lần mở kế tiếp sẽ thử lại.
      return 0;
    }
  }, [readOnly, reload]);
}

/** Gắn MỘT lần ở khung ứng dụng: chạy lúc vào app và mỗi lần sang ngày mới. */
export function useDailyScheduleGeneration(today: CalendarDate): void {
  const generate = useGenerateScheduledTasks();
  const { message } = App.useApp();
  const previousDay = useRef<string | null>(null);
  // Khoá theo NGÀY, không theo đối tượng `today`: đồng hồ đổi từng phút nhưng chỉ sang ngày mới
  // mới cần sinh việc lại.
  const dayLabel = formatDayMonth(today);
  const dayKey = `${today.year}-${dayLabel}`;

  useEffect(() => {
    const isRollover = previousDay.current !== null && previousDay.current !== dayKey;
    previousDay.current = dayKey;
    void generate().then((created) => {
      if (isRollover) {
        void message.info(
          `Sang ngày mới ${dayLabel}` + (created ? ' — đã tạo việc theo lịch' : ''),
        );
      }
    });
  }, [dayKey, dayLabel, generate, message]);
}
